/* eslint-disable camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import MenuList from '../components/menus/menu-list';
import addResourcesFallbackUIProps from './group-fallback-ui';
import { Resource, getResourceBehavior } from '../models/group';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  cancelAllExisting,
  selectAllExisting, toggleAll, toggleItem, updateSearch,
} from './menu-list.slice';
import {
  getAvailableItems,
  getHasNothingAvailable, getHasSelectedAllExisting,
  getPage,
  getSearchString,
  getSelectedItems,
} from './menu-list.selectors';

interface Props {
  id: string;
  resource: Resource;
  alreadyAdded: string[];
}

export default function GroupMenuList({ resource, id, alreadyAdded = [] }: Props) {
  const resourceBehavior = getResourceBehavior(resource);
  const defaultParams = { per_page: 1000, exclude_group_id: [id], basic_info: true };

  const dispatch = useAppDispatch();

  const availableItems = useAppSelector(getAvailableItems);
  const selectedItems = useAppSelector(getSelectedItems);
  const hasAllExisting = useAppSelector(getHasSelectedAllExisting);
  const hasNoResourceAvailable = useAppSelector(getHasNothingAvailable);
  const search = useAppSelector(getSearchString);
  const page = useAppSelector(getPage);

  const {
    data,
    isLoading,
    isFetching,
    isError,
  } = resourceBehavior.useQuery(
    { ...defaultParams, search, page },
    { refetchOnMountOrArgChange: true },
  );

  const handleSearch = useCallback((newSearch) => {
    dispatch(updateSearch(newSearch));
  }, []);

  const handleToggleSelect = useCallback((item) => {
    dispatch(toggleItem(item));
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    dispatch(toggleAll());
  }, []);

  const handleSelectAllExisting = useCallback(() => {
    dispatch(selectAllExisting());
  }, []);

  const handleUnselectAllExisting = useCallback(() => {
    dispatch(cancelAllExisting());
  }, []);

  return (
    <Stack width={500}>
      <MenuList
        items={availableItems}
        checkedItems={selectedItems}
        isLoading={isLoading}
        isFetching={isFetching}
        isSearching={!!search}
        errorMessage={resourceBehavior.getErrorMessage(isError)}
        searchPlaceholder={resourceBehavior.getPlaceholderText()}
        search={search}
        onSearch={handleSearch}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        onSelectAllExisting={handleSelectAllExisting}
        allExistingCount={data?.total_count || 0}
        pageLimit={defaultParams.per_page}
        unselectAllExisting={handleUnselectAllExisting}
        hasAllExisting={hasAllExisting}
        poster={addResourcesFallbackUIProps({
          resource,
          isAllAdded: !!alreadyAdded.length && hasNoResourceAvailable,
          isSearching: !!search,
          zeroResults: hasNoResourceAvailable,
          zeroResourcesInOrg: !alreadyAdded.length && hasNoResourceAvailable,
        })}
      />
    </Stack>
  );
}
